// Kiểm tra có phải mảng dữ liệu các field cần thiết
function isAggregateStatsArray(value) {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  const first = value[0];
  if (!first || typeof first !== "object") {
    return false;
  }
  if (!("count" in first) || !("label" in first)) {
    return false;
  }
  const hasDvvcShape = "dvvc_key" in first;
  const hasStatusShape = "status" in first;
  return hasDvvcShape || hasStatusShape;
}

//  Format Date object thành chuỗi YYYY-MM-DD (tránh lệch timezone)
function formatLocalIsoDate(dateObj) {
  const y = dateObj.getFullYear();
  const m = `${dateObj.getMonth() + 1}`.padStart(2, "0");
  const d = `${dateObj.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Tạo danh sách tất cả các ngày giữa start và end
function eachDayInclusive(startIso, endIso) {
  const days = [];
  const startParts = startIso.split("-").map(Number);
  const endParts = endIso.split("-").map(Number);
  if (startParts.length !== 3 || endParts.length !== 3) {
    return days;
  }
  const cursor = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor > end) {
    return days;
  }
  const walk = new Date(cursor.getTime());
  while (walk <= end) {
    days.push(formatLocalIsoDate(walk));
    walk.setDate(walk.getDate() + 1);
  }
  return days;
}

// Convert dữ liệu aggregate thành 1 dòng timeline cho 1 ngày cụ thể
function buildOneRowFromAggregateItems(items, dayIso) {
  const isDvvcData = Boolean(items[0] && "dvvc_key" in items[0]);

  /** @type {Record<string, number>} */
  const templateStatus = {};
  /** @type {Record<string, number>} */
  const templateCarrier = {};

  items.forEach(item => {
    const count = Number(item.count) || 0;
    const labelKey = item.label;
    if (isDvvcData) {
      templateCarrier[labelKey] = count;
    } else {
      templateStatus[labelKey] = count;
    }
  });

  return {
    date: dayIso,
    statusCounts: { ...templateStatus },
    carrierCounts: { ...templateCarrier },
  };
}

function buildRowsFromAggregateStats(items, referenceRange) {
  const targetDate =
    referenceRange?.endDate ||
    referenceRange?.startDate ||
    formatLocalIsoDate(new Date());

  return [buildOneRowFromAggregateItems(items, targetDate)];
}

// Public: danh sách ngày liên tục giữa hai ISO (local).
export function listDaysInclusive(startIso, endIso) {
  return eachDayInclusive(startIso, endIso);
}

export const MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS = 15;

export function clampDateRangeToMaxDays(dateRange, maxDays = MAX_DASHBOARD_TIMELINE_LINE_CHART_RANGE_DAYS) {
  const startDate = String(dateRange?.startDate ?? "").trim();
  const endDate = String(dateRange?.endDate ?? "").trim();

  if (!startDate || !endDate || maxDays < 1) {
    return { startDate, endDate };
  }

  const dayList = listDaysInclusive(startDate, endDate);

  if (!dayList.length || dayList.length <= maxDays) {
    return { startDate, endDate };
  }

  return {
    startDate: dayList[dayList.length - maxDays],
    endDate,
  };
}

export function resolveDateRangeFromTimelineRows(timelineRows) {
  if (!Array.isArray(timelineRows) || !timelineRows.length) {
    return { startDate: "", endDate: "" };
  }

  const dates = timelineRows
    .map(rowItem => (rowItem?.date ? String(rowItem.date).trim() : ""))
    .filter(Boolean)
    .sort();

  if (!dates.length) {
    return { startDate: "", endDate: "" };
  }

  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1],
  };
}

function unwrapResponseEnvelope(rawResponse) {
  if (!rawResponse || typeof rawResponse !== "object") {
    return rawResponse;
  }
  const firstLayer = rawResponse.data !== undefined ? rawResponse.data : rawResponse;
  const firstLayerHasTimeline =
    firstLayer &&
    typeof firstLayer === "object" &&
    !Array.isArray(firstLayer) &&
    (Array.isArray(firstLayer.timeline) ||
      Array.isArray(firstLayer.data_timeline) ||
      Array.isArray(firstLayer.daily) ||
      Array.isArray(firstLayer.rows));

  if (firstLayerHasTimeline) {
    return firstLayer;
  }

  if (
    firstLayer &&
    typeof firstLayer === "object" &&
    firstLayer.data !== undefined &&
    !Array.isArray(firstLayer)
  ) {
    const inner = firstLayer.data;
    const innerLooksLikeChart =
      inner &&
      typeof inner === "object" &&
      (inner.series ||
        inner.categories ||
        inner.labels ||
        inner.dates ||
        inner.timeline ||
        inner.daily ||
        inner.items ||
        inner.rows ||
        Array.isArray(inner));
    if (innerLooksLikeChart) {
      return inner;
    }
  }
  return firstLayer;
}

function readInclusiveDaySpanFromMetaPayload(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return 0;
  }

  const keyPairs = [
    ["from_time", "to_time"],
    ["from_date", "to_date"],
    ["start_date", "end_date"],
    ["date_from", "date_to"],
  ];

  const tryObject = obj => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
      return 0;
    }
    for (let pairIndex = 0; pairIndex < keyPairs.length; pairIndex += 1) {
      const startKey = keyPairs[pairIndex][0];
      const endKey = keyPairs[pairIndex][1];
      const startRaw = obj[startKey];
      const endRaw = obj[endKey];
      if (startRaw == null || endRaw == null) {
        continue;
      }
      const startIso = normalizeDateString(startRaw);
      const endIso = normalizeDateString(endRaw);
      if (!startIso || !endIso) {
        continue;
      }
      const days = listDaysInclusive(startIso, endIso);
      if (days.length > 0) {
        return days.length;
      }
    }
    return 0;
  };

  let spanDays = tryObject(payload);
  if (spanDays > 0) {
    return spanDays;
  }

  if (
    payload
    && typeof payload === "object"
    && !Array.isArray(payload)
    && "data" in payload
    && payload.data
    && typeof payload.data === "object"
    && !Array.isArray(payload.data)
  ) {
    spanDays = tryObject(payload.data);
  }

  return spanDays;
}

const FILTER_APPLIED_DATE_KEY_PAIRS = [
  ["from_time", "to_time"],
  ["from_date", "to_date"],
  ["start_date", "end_date"],
  ["date_from", "date_to"],
];

function readDateRangeFromObject(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return null;
  }

  for (let pairIndex = 0; pairIndex < FILTER_APPLIED_DATE_KEY_PAIRS.length; pairIndex += 1) {
    const startKey = FILTER_APPLIED_DATE_KEY_PAIRS[pairIndex][0];
    const endKey = FILTER_APPLIED_DATE_KEY_PAIRS[pairIndex][1];
    const startIso = normalizeDateString(obj[startKey]);
    const endIso = normalizeDateString(obj[endKey]);

    if (startIso && endIso) {
      return { startDate: startIso, endDate: endIso };
    }
  }

  return null;
}

export function readFilterAppliedDateRange(rawResponse) {
  if (!rawResponse || typeof rawResponse !== "object") {
    return { startDate: "", endDate: "" };
  }

  const responseBody =
    rawResponse.data !== undefined && typeof rawResponse.data === "object"
      ? rawResponse.data
      : rawResponse;

  const candidates = [
    responseBody.filter_applied,
    responseBody.filterApplied,
    rawResponse.filter_applied,
    rawResponse.filterApplied,
  ];

  if (
    responseBody.data &&
    typeof responseBody.data === "object" &&
    !Array.isArray(responseBody.data)
  ) {
    candidates.push(responseBody.data.filter_applied);
    candidates.push(responseBody.data.filterApplied);
    candidates.push(responseBody.data);
  }

  for (let index = 0; index < candidates.length; index += 1) {
    const resolvedRange = readDateRangeFromObject(candidates[index]);
    if (resolvedRange) {
      return resolvedRange;
    }
  }

  return { startDate: "", endDate: "" };
}

// Độ rộng từ min→max của các dòng timeline đã parse.
function readInclusiveDaySpanFromTimelineRows(timelineRows) {
  if (!Array.isArray(timelineRows) || !timelineRows.length) {
    return 0;
  }
  const dates = timelineRows
    .map(rowItem => (rowItem && rowItem.date ? String(rowItem.date).trim() : ""))
    .filter(Boolean)
    .sort();
  if (!dates.length) {
    return 0;
  }
  if (dates.length === 1) {
    return 1;
  }
  const days = listDaysInclusive(dates[0], dates[dates.length - 1]);
  return days.length;
}

// Số ngày thống kê: lấy max(UI range, meta API, min/max dữ liệu)
export function getDashboardStatsTimelineSpanDays(rawResponse, timelineRows, referenceRange) {
  let referenceSpanDays = 0;
  if (referenceRange?.startDate && referenceRange?.endDate) {
    const refDays = listDaysInclusive(referenceRange.startDate, referenceRange.endDate);
    referenceSpanDays = refDays.length;
  }

  const payload = unwrapResponseEnvelope(rawResponse);
  const metaSpanDays = readInclusiveDaySpanFromMetaPayload(payload);
  const dataSpanDays = readInclusiveDaySpanFromTimelineRows(timelineRows);

  return Math.max(referenceSpanDays, metaSpanDays, dataSpanDays);
}

// Chuẩn hoá chuỗi ngày từ API về YYYY-MM-DD.
function normalizeDateString(value) {
  if (!value) {
    return "";
  }
  const stringValue = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
    return stringValue.slice(0, 10);
  }
  const parts = stringValue.split(/[/|-]/);
  if (parts.length === 3 && parts[2].length === 4) {
    const day = parts[0].padStart(2, "0");
    const month = parts[1].padStart(2, "0");
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }
  const parsed = new Date(stringValue);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().split("T")[0];
  }
  return stringValue;
}

// Lấy map số liệu theo status/DVVC 
function collectBagFromRow(row) {
  if (!row || typeof row !== "object") {
    return null;
  }
  const preferred =
    row.by_status ||
    row.status_counts ||
    row.by_dvvc ||
    row.dvvc_counts ||
    row.carrier_counts ||
    row.counts ||
    row.metrics;
  if (preferred && typeof preferred === "object" && !Array.isArray(preferred)) {
    return preferred;
  }
  const META = new Set([
    "date",
    "day",
    "from_time",
    "to_time",
    "id",
    "hub_id",
    "order_type",
    "created_at",
    "updated_at",
  ]);
  const flat = {};
  for (const [key, value] of Object.entries(row)) {
    if (META.has(key)) {
      continue;
    }
    if (typeof value === "number") {
      flat[key] = value;
    }
  }
  return Object.keys(flat).length ? flat : null;
}

// Convert object thành mảng dòng timeline đã sort
function buildRowsFromTimelineObject(timelineObject, mode) {
  if (!timelineObject || typeof timelineObject !== "object" || Array.isArray(timelineObject)) {
    return null;
  }

  const rows = Object.entries(timelineObject)
    .map(([dateKey, rowValue]) => {
      const normalizedDate = normalizeDateString(dateKey);
      const bag = collectBagFromRow(rowValue);
      /** @type {Record<string, number>} */
      const statusCounts = {};
      /** @type {Record<string, number>} */
      const carrierCounts = {};

      if (bag) {
        Object.entries(bag).forEach(([key, value]) => {
          const numericValue = Number(value) || 0;
          if (mode === "dvvc") {
            carrierCounts[String(key)] = numericValue;
          } else {
            statusCounts[String(key)] = numericValue;
          }
        });
      }

      return {
        date: normalizedDate,
        statusCounts,
        carrierCounts,
      };
    })
    .filter(rowItem => rowItem.date);

  if (!rows.length) {
    return null;
  }

  return rows.sort((a, b) => a.date.localeCompare(b.date));
}

// Dạng Apex categories + series → dòng timeline theo ngày.
function buildRowsFromCategoriesSeries(payload, mode) {
  const categoriesRaw =
    payload.categories || payload.labels || payload.dates || payload.timeline_dates || [];
  const seriesRaw = payload.series || payload.datasets || payload.lines || [];
  if (!Array.isArray(categoriesRaw) || !Array.isArray(seriesRaw) || !seriesRaw.length) {
    return null;
  }

  const days = categoriesRaw.map(categoryItem => normalizeDateString(categoryItem));
  const maxLen = Math.max(
    days.length,
    ...seriesRaw.map(serieItem => (Array.isArray(serieItem.data) ? serieItem.data.length : 0))
  );

  const rows = [];
  for (let index = 0; index < maxLen; index += 1) {
    const dateKey = days[index] || "";
    if (!dateKey) {
      continue;
    }
    /** @type {Record<string, number>} */
    const statusCounts = {};
    /** @type {Record<string, number>} */
    const carrierCounts = {};
    seriesRaw.forEach(serieItem => {
      const serieName = serieItem.name ?? serieItem.label ?? serieItem.key ?? "";
      const dataPoints = Array.isArray(serieItem.data) ? serieItem.data : [];
      const numericValue = Number(dataPoints[index] ?? 0);
      if (mode === "dvvc") {
        carrierCounts[String(serieName)] = numericValue;
      } else {
        statusCounts[String(serieName)] = numericValue;
      }
    });
    rows.push({ date: dateKey, statusCounts, carrierCounts });
  }
  return rows.length ? rows.sort((a, b) => a.date.localeCompare(b.date)) : null;
}

// Convert dữ liệu daily/bucket thành { date, statusCounts, carrierCounts }
function transformDayRow(row, mode) {
  const rawDate = row.date ?? row.day ?? row.from_time ?? row.bucket_date;
  const dateKey = normalizeDateString(rawDate);
  const bag = collectBagFromRow(row);
  /** @type {Record<string, number>} */
  const statusCounts = {};
  /** @type {Record<string, number>} */
  const carrierCounts = {};

  if (bag) {
    Object.entries(bag).forEach(([key, value]) => {
      const numericValue = Number(value) || 0;
      if (mode === "dvvc") {
        carrierCounts[String(key)] = numericValue;
      } else {
        statusCounts[String(key)] = numericValue;
      }
    });
  }

  return { date: dateKey, statusCounts, carrierCounts };
}

// Convert dữ liệu response stats thành mảng dòng { date, statusCounts, carrierCounts }
export function buildTimelineFromStatsResponse(rawResponse, mode, options) {
  if (!rawResponse) {
    return [];
  }

  const payload = unwrapResponseEnvelope(rawResponse);

  if (!payload) {
    return [];
  }

  if (Array.isArray(payload) && isAggregateStatsArray(payload)) {
    return buildRowsFromAggregateStats(payload, options?.referenceRange);
  }

  const fromCategories = buildRowsFromCategoriesSeries(payload, mode);
  if (fromCategories) {
    return fromCategories;
  }

  const timelineObjectSource = payload.data_timeline || payload.timeline;
  const fromTimelineObject = buildRowsFromTimelineObject(
    timelineObjectSource,
    mode
  );
  if (fromTimelineObject) {
    return fromTimelineObject;
  }

  const listCandidate =
    payload.data_timeline ||
    payload.timeline ||
    payload.daily ||
    payload.items ||
    payload.rows ||
    payload.buckets ||
    payload.data;

  if (Array.isArray(listCandidate) && listCandidate.length) {
    if (isAggregateStatsArray(listCandidate)) {
      return buildRowsFromAggregateStats(listCandidate, options?.referenceRange);
    }
    return listCandidate
      .map(rowItem => transformDayRow(rowItem, mode))
      .filter(rowItem => rowItem.date)
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  if (Array.isArray(payload)) {
    return payload
      .map(rowItem => transformDayRow(rowItem, mode))
      .filter(rowItem => rowItem.date)
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  return [];
}
